/**
 * LR-528 re-rate mapper.
 *
 * Re-rate changes ONLY the rate leg of an ACTIVE (or backdated MATURED) position
 * via POST /positions/{id}/re-rate/ (LR-526/527). Unlike amend, the mode is NOT
 * re-rateable — it is fixed by the booked `rate_entry_mode`:
 *   rebate → `benchmark_rate` / `spread_bps`
 *   fee    → `fee_percent`
 * This is the FE half of the backend PositionReRateSerializer allow-list; the
 * `_MODE_FIELDS` map here mirrors the serializer's. Every read of the booked
 * PositionDetail is coerced defensively — decimal columns are typed `string` but
 * arrive as JS numbers at runtime (recurring LR gotcha).
 */
/**
 * Structural subset of the rate fields the mapper reads — so BOTH callers fit
 * without a cast: `PositionDetail` (the single Re-rate modal) and `TradeItem`
 * (the bulk staging grid) each carry these fields (LR-539 added rate_type /
 * benchmark_rate to the blotter list). Decimal columns arrive as string OR
 * number at runtime, hence the permissive value types (every read is coerced).
 */
export interface ReRateableRate {
  rate_entry_mode?: string | null;
  rate_type?: string | null;
  benchmark_rate?: number | string | null;
  spread_bps?: number | string | null;
  fee_percent?: number | string | null;
  /** LR-628: served by both position serializers — the backend's own answer to
   *  "does the nightly accrual own this benchmark?". Narrow `boolean`, matching
   *  the three real interfaces: the backend field is a plain bool and can never
   *  arrive null, and widening it here would invent a third state. */
  benchmark_follows_fixing?: boolean;
}

const str = (v: unknown, fallback = ''): string =>
  v === null || v === undefined ? fallback : String(v);

const num = (v: unknown): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
};

/** Rate-entry mode. Fixed by the booked position — a re-rate can never flip it. */
export type ReRateMode = 'rebate' | 'fee';

export const reRateMode = (trade: ReRateableRate): ReRateMode =>
  trade.rate_entry_mode === 'fee' ? 'fee' : 'rebate';

export type ReRateFieldKey = 'benchmark_rate' | 'spread_bps' | 'fee_percent';

/**
 * Does the nightly accrual, rather than the booking, own this trade's benchmark?
 *
 * Reads the backend's `benchmark_follows_fixing` — which answers OWNERSHIP, not
 * its cause: the server returns `_benchmark_from_fixing(obj) or
 * obj.cost_follows_pool()`, so a pooled GMSLA fee trade reports true for a cost
 * leg priced off its CASH POOL, not off a fixing (LR-631). A caller that merely
 * withholds an input can take the flag as-is; one that CAPTIONS the number must
 * establish the cause itself — the bulk grid does it with a `reRateMode` gate.
 *
 * One owner for a rule two surfaces now read: the allow-list below (may a human
 * SET this number?) and the grid's read-only cell (is this number the trader's?).
 * Derived separately they agree only until the allow-list grows a second reason to
 * withhold the input, at which point the cell would speak for a rate nothing in
 * the engine owns (LR-652).
 *
 * Gated on the backend's own flag, NOT on rate_type: a GMRA floating repo and a
 * floating rebate on a feedless benchmark (TONAR/HONIA/OTHER) still accrue on
 * their BOOKED benchmark. Absent (a stale cached payload, a stub) FAILS CLOSED
 * onto the common case — see the asymmetry argued at the call site below.
 */
export function benchmarkFollowsFixing(trade: ReRateableRate): boolean {
  return trade.benchmark_follows_fixing ?? trade.rate_type === 'floating';
}

/**
 * The rate fields a trade may re-rate, gated by the (rate_entry_mode, rate_type)
 * matrix — NOT mode alone:
 *   rebate + fixed             → benchmark_rate ONLY (a spread on a fixed trade
 *                                would flip calculate_total_rate to the
 *                                benchmark+spread branch — a silent rate_type
 *                                change, which the product forbids)
 *   rebate, fixing-driven      → spread_bps ONLY (LR-628, see below)
 *   rebate + floating, else    → benchmark_rate and/or spread_bps
 *   fee                        → fee_percent
 * Belt-and-suspenders on the FE: the backend serializer currently gates by mode
 * only, so offering spread on a rebate-fixed trade would be silently accepted.
 */
export function reRateEditableFields(trade: ReRateableRate): ReRateFieldKey[] {
  if (reRateMode(trade) === 'fee') return ['fee_percent'];
  if (trade.rate_type === 'fixed') return ['benchmark_rate'];
  // LR-628: where the nightly accrual resolves the benchmark itself, writing one
  // by hand would move the audit/fallback value while changing nothing about
  // what accrues tomorrow — the backend refuses it, and in bulk that 400 fails
  // the WHOLE batch. So the spread, the part of such a rate a human owns, is the
  // only field offered.
  //
  // The two directions are not symmetric, which is why the predicate fails closed:
  // withholding the field costs a repo trader one input, whereas offering it on a
  // fixing-driven trade posts a value the backend refuses — and in bulk that 400
  // fails the whole transaction, so one stale row would take the other 199 with it.
  return benchmarkFollowsFixing(trade) ? ['spread_bps'] : ['benchmark_rate', 'spread_bps'];
}

/**
 * Soft typo threshold: a resulting rate below this is a likely sign/magnitude
 * fat-finger, not a real quote. Non-blocking everywhere — the backend accepts the
 * value (the serializer band is ±10000 %), so this is a "look again", not a gate.
 *
 * One constant and one predicate for all three surfaces that warn — trade entry's
 * Rebate Rate field, the single Re-Rate modal, and the bulk grid — so the number
 * and the rule cannot drift apart while the wording stays per-surface (the bulk
 * grid counts rows; the other two describe one rate).
 */
export const LOW_RATE_WARN_PCT = -10;

/** Bare numeric test against the threshold. NaN and undefined are both "nothing to
 *  warn about" — the field is mid-edit or empty. Trade entry uses this directly on
 *  the value being typed; the re-rate surfaces go through `warnsLowResultingRate`,
 *  which adds the mode rule on top. */
export function isLowRateWarning(rate: number | undefined | null): boolean {
  return rate !== undefined && rate !== null && Number.isFinite(rate) && rate < LOW_RATE_WARN_PCT;
}

/**
 * The re-rate policy: warn on a resulting TOTAL rate below the threshold, but only
 * in rebate mode.
 *
 * Fee mode is excluded deliberately, and the arithmetic is why, not just taste.
 * There `total = fee_percent − benchmark_rate` with `fee_percent` hard-floored above
 * 0 (`breachesFeeFloor`), so:
 *   - against a real benchmark (SOFR/OBFR/SONIA/ESTR/TONAR/HONIA, all far under 10 %)
 *     the total can never reach −10 % at all — the branch is dead;
 *   - against a benchmark above 10 % ('other', or a hand-entered rate) it fires on a
 *     perfectly ordinary fee edit and CANNOT be cleared, because the only editable
 *     field is already at its floor.
 * Dead in the common case, un-actionable in the rare one. The sign of a fee-mode
 * total is set by the benchmark, which a re-rate cannot touch, so there is no
 * fat-finger here for the guard to catch.
 *
 * Lives here rather than at the call sites so the single modal and the bulk grid
 * cannot answer the same question differently — the same reason `breachesFeeFloor`
 * takes the trade instead of a pre-filtered value.
 */
export function warnsLowResultingRate(
  trade: ReRateableRate,
  totalRate: number | undefined | null,
): boolean {
  if (reRateMode(trade) === 'fee') return false;
  return isLowRateWarning(totalRate);
}

/**
 * `fee_percent` must stay STRICTLY positive, so a fee row heading for 0 or below
 * is gated before the POST rather than after a guaranteed 400. Two backend rules
 * produce that: the serializers floor the field at 0 (`min_value=Decimal('0')` on
 * both PositionReRateSerializer and BulkReRateEntrySerializer), and an exact 0
 * trips PositionReRateSerializer's "Fee is required for Fee mode". In bulk either
 * outcome fails the WHOLE batch (the view returns `failed[]` from inside
 * `transaction.atomic()`), so a single bad row costs every other row in it.
 *
 * Scoped by the same allow-list `buildReRatePayload` posts through: a fee value
 * left on a row whose mode does not use one is never sent, so it must never gate
 * either. Reachable via a negative adjust-bps over a fee row, or a typed 0 (LR-623).
 */
export function breachesFeeFloor(
  trade: ReRateableRate,
  values: Pick<ReRateFormValues, 'fee_percent'>,
): boolean {
  if (!reRateEditableFields(trade).includes('fee_percent')) return false;
  const fee = num(values.fee_percent);
  return fee !== undefined && fee <= 0;
}

/** The live editable form state. Effective date is always present; the rate
 *  fields depend on mode. `reason` is captured but the MVP sends it blank
 *  (reason dropdown is out of scope — LR-528). */
export interface ReRateFormValues {
  effective_date: string;
  benchmark_rate?: number;
  spread_bps?: number;
  fee_percent?: number;
  reason?: string;
}

/** POST body for /positions/{id}/re-rate/. Decimals go as strings, spread as a
 *  signed int — exactly the wire types PositionReRateSerializer expects. */
export interface ReRatePayload {
  effective_date: string;
  benchmark_rate?: string;
  spread_bps?: number;
  fee_percent?: string;
  reason?: string;
}

/** Numeric change test — money/int compare NUMERICALLY so "180.00" === 180 never
 *  reads as a change (trailing-zero / type drift). */
const changed = (formVal: unknown, origVal: unknown): boolean => {
  const a = num(formVal);
  const b = num(origVal);
  if (a === undefined && b === undefined) return false;
  return a !== b;
};

/**
 * The POST body: effective_date + only the mode's rate fields that ACTUALLY
 * changed vs the booked position. Sending just the moved fields keeps the audit
 * `re_rated` change payload clean (no before==after noise) and mirrors amend's
 * diff-only contract. The backend applies a partial change fine.
 */
export function buildReRatePayload(trade: ReRateableRate, values: ReRateFormValues): ReRatePayload {
  const payload: ReRatePayload = { effective_date: values.effective_date };
  const editable = reRateEditableFields(trade);
  const nextBenchmark = num(values.benchmark_rate);
  const nextSpread = num(values.spread_bps);
  const nextFee = num(values.fee_percent);
  // Only emit a rate field that holds a REAL number AND moved vs the booked
  // value. A cleared input (DecimalInput → undefined) means "leave booked" on the
  // wire — matching the preview's `?? booked` fallback. Without the number guard a
  // cleared spread would post 0 (a silent rate change the preview never showed)
  // and a cleared benchmark/fee would post '' (backend 400). LR-528 self-review R2.
  if (editable.includes('fee_percent') && nextFee !== undefined && changed(values.fee_percent, trade.fee_percent)) {
    payload.fee_percent = str(values.fee_percent);
  }
  if (
    editable.includes('benchmark_rate') &&
    nextBenchmark !== undefined &&
    changed(values.benchmark_rate, trade.benchmark_rate)
  ) {
    payload.benchmark_rate = str(values.benchmark_rate);
  }
  if (editable.includes('spread_bps') && nextSpread !== undefined && changed(values.spread_bps, trade.spread_bps)) {
    payload.spread_bps = nextSpread;
  }
  const reason = values.reason?.trim();
  if (reason) payload.reason = reason;
  return payload;
}

/** At least one rate field moved — gates the Confirm button so a no-op never
 *  fires (mirrors amend's `hasChanges`). effective_date alone is not a change. */
export function hasRateChange(payload: ReRatePayload): boolean {
  return (
    payload.benchmark_rate !== undefined ||
    payload.spread_bps !== undefined ||
    payload.fee_percent !== undefined
  );
}
